import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Calculator, Bot, Sparkles, Search, FileText, DollarSign, BookOpen, GraduationCap, ChevronDown, ChevronUp, ExternalLink, ClipboardList, CheckCircle, AlertCircle, Download, Loader2, Plus, Trash2, Edit, Receipt, Briefcase, TrendingUp } from "lucide-react";

const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);

export default function TaxCenter() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  
  // Tax Liability Estimator
  const [liabilityResult, setLiabilityResult] = useState(null);
  const [liabilityLoading, setLiabilityLoading] = useState(false);
  const [liabilityForm, setLiabilityForm] = useState({
    filing_status: "single",
    additional_deductions: "",
    tax_year: 2025,
    state: ""
  });

  // Tax Estimate Form
  const [taxForm, setTaxForm] = useState({
    filing_status: "single",
    total_income: "",
    business_income: "",
    deductions: "",
    state: ""
  });
  
  // IRS Search
  const [irsQuery, setIrsQuery] = useState("");
  const [irsResult, setIrsResult] = useState(null);
  
  // Pricing Analysis
  const [pricingForm, setPricingForm] = useState({
    business_type: "",
    product_service: "",
    costs: ""
  });
  const [pricingResult, setPricingResult] = useState(null);
  
  // Tax Education
  const [educationTopic, setEducationTopic] = useState("");
  const [educationResult, setEducationResult] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);

  // 2026 Tax Brackets and Key Information
  const taxBrackets2026 = {
    single: [
      { rate: "10%", range: "$0 - $11,925", tax: "10% of income" },
      { rate: "12%", range: "$11,926 - $48,475", tax: "$1,192.50 + 12% over $11,925" },
      { rate: "22%", range: "$48,476 - $103,350", tax: "$5,578.50 + 22% over $48,475" },
      { rate: "24%", range: "$103,351 - $197,300", tax: "$17,651 + 24% over $103,350" },
      { rate: "32%", range: "$197,301 - $250,525", tax: "$40,199 + 32% over $197,300" },
      { rate: "35%", range: "$250,526 - $626,350", tax: "$57,231 + 35% over $250,525" },
      { rate: "37%", range: "Over $626,350", tax: "$188,769.75 + 37% over $626,350" }
    ],
    married_jointly: [
      { rate: "10%", range: "$0 - $23,850", tax: "10% of income" },
      { rate: "12%", range: "$23,851 - $96,950", tax: "$2,385 + 12% over $23,850" },
      { rate: "22%", range: "$96,951 - $206,700", tax: "$11,157 + 22% over $96,950" },
      { rate: "24%", range: "$206,701 - $394,600", tax: "$35,302 + 24% over $206,700" },
      { rate: "32%", range: "$394,601 - $501,050", tax: "$80,398 + 32% over $394,600" },
      { rate: "35%", range: "$501,051 - $751,600", tax: "$114,462 + 35% over $501,050" },
      { rate: "37%", range: "Over $751,600", tax: "$202,154.50 + 37% over $751,600" }
    ]
  };

  const standardDeductions2026 = [
    { status: "Single", amount: "$15,000", increase: "+$400 from 2025" },
    { status: "Married Filing Jointly", amount: "$30,000", increase: "+$800 from 2025" },
    { status: "Married Filing Separately", amount: "$15,000", increase: "+$400 from 2025" },
    { status: "Head of Household", amount: "$22,500", increase: "+$600 from 2025" }
  ];

  const retirementLimits2026 = [
    { account: "401(k) / 403(b)", limit: "$23,500", catchUp: "$7,500 (age 50+)" },
    { account: "IRA (Traditional/Roth)", limit: "$7,000", catchUp: "$1,000 (age 50+)" },
    { account: "SIMPLE IRA", limit: "$16,500", catchUp: "$3,500 (age 50+)" },
    { account: "SEP IRA", limit: "25% of comp, max $69,000", catchUp: "N/A" },
    { account: "HSA (Self)", limit: "$4,300", catchUp: "$1,000 (age 55+)" },
    { account: "HSA (Family)", limit: "$8,550", catchUp: "$1,000 (age 55+)" }
  ];

  const keyDeadlines2026 = [
    { date: "January 15, 2026", event: "Q4 2025 Estimated Tax Due", description: "Final quarterly estimated tax payment for 2025" },
    { date: "January 31, 2026", event: "W-2 and 1099 Forms Due", description: "Employers must send W-2s; 1099-NEC/MISC due" },
    { date: "April 15, 2026", event: "Tax Filing Deadline", description: "Individual returns due (or extension)" },
    { date: "April 15, 2026", event: "Q1 2026 Estimated Tax Due", description: "First quarterly payment for 2026" },
    { date: "June 15, 2026", event: "Q2 2026 Estimated Tax Due", description: "Second quarterly payment" },
    { date: "September 15, 2026", event: "Q3 2026 Estimated Tax Due", description: "Third quarterly payment" },
    { date: "October 15, 2026", event: "Extended Returns Due", description: "Deadline for taxpayers who filed extensions" }
  ];

  const taxCredits2026 = [
    { credit: "Child Tax Credit", amount: "Up to $2,000/child", requirement: "Children under 17, income limits apply" },
    { credit: "Earned Income Tax Credit", amount: "Up to $7,830", requirement: "Low-moderate income, varies by dependents" },
    { credit: "Child & Dependent Care", amount: "Up to $3,000 (1) / $6,000 (2+)", requirement: "Care expenses for work/school" },
    { credit: "Education Credits (AOTC)", amount: "Up to $2,500", requirement: "First 4 years of college" },
    { credit: "Lifetime Learning Credit", amount: "Up to $2,000", requirement: "Any post-secondary education" },
    { credit: "Saver's Credit", amount: "Up to $1,000 ($2,000 MFJ)", requirement: "Retirement contributions, income limits" },
    { credit: "EV Tax Credit", amount: "Up to $7,500", requirement: "New qualifying electric vehicles" },
    { credit: "Energy Efficient Home", amount: "Up to $3,200", requirement: "Heat pumps, insulation, solar panels" }
  ];

  const newFor2026 = [
    { 
      title: "Inflation Adjustments", 
      description: "Tax brackets, standard deduction, and many credits adjusted ~3% for inflation",
      impact: "May result in slightly lower tax liability for same income"
    },
    {
      title: "1099-K Threshold",
      description: "$600 threshold for payment app reporting (Venmo, PayPal, etc.) fully in effect",
      impact: "More taxpayers will receive 1099-K forms for online sales"
    },
    {
      title: "Beneficial Ownership Information (BOI)",
      description: "New FinCEN reporting requirements for small businesses",
      impact: "Most LLCs/Corps must file ownership reports"
    },
    {
      title: "Digital Asset Reporting",
      description: "Enhanced crypto/digital asset reporting requirements",
      impact: "Exchanges must report transactions; basis tracking required"
    },
    {
      title: "Clean Vehicle Credits",
      description: "Updated qualifying vehicles and income limits for EV credits",
      impact: "Check IRS.gov for current qualifying vehicles list"
    }
  ];

  const handleEducationSearch = async () => {
    if (!educationTopic.trim()) {
      toast.error("Please enter a tax topic");
      return;
    }
    setLoading(true);
    setEducationResult(null);
    try {
      const res = await api.post(`/ai/tax-education`, { topic: educationTopic });
      setEducationResult(res.data.education);
    } catch (e) {
      toast.error("Failed to get education content");
    } finally {
      setLoading(false);
    }
  };

  // Form Assistant State
  const [formAssistantStep, setFormAssistantStep] = useState(1);
  const [userProfile, setUserProfile] = useState({
    filing_status: "single",
    has_w2_income: false,
    has_self_employment: false,
    has_rental_property: false,
    has_investments: false,
    has_itemized_deductions: false,
    has_dependents: false,
    num_dependents: 0,
    has_retirement_contributions: false,
    has_health_savings: false,
    has_education_expenses: false,
    has_home_mortgage: false,
    has_charitable_donations: false,
  });
  const [requiredForms, setRequiredForms] = useState(null);
  const [formData, setFormData] = useState(null);
  const [filledForms, setFilledForms] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  // IRS Form Database
  const irsFormsDatabase = {
    "1040": {
      name: "Form 1040",
      title: "U.S. Individual Income Tax Return",
      description: "Main individual tax return - required for all filers",
      pdfUrl: "https://www.irs.gov/pub/irs-pdf/f1040.pdf",
      everyone: true
    },
    "Schedule1": {
      name: "Schedule 1",
      title: "Additional Income and Adjustments",
      description: "Report additional income (alimony, business income) and adjustments (student loan interest, IRA deductions)",
      pdfUrl: "https://www.irs.gov/pub/irs-pdf/f1040s1.pdf",
      condition: (p) => p.has_self_employment || p.has_retirement_contributions || p.has_health_savings
    },
    "ScheduleA": {
      name: "Schedule A",
      title: "Itemized Deductions",
      description: "Medical expenses, state/local taxes (SALT), mortgage interest, charitable contributions",
      pdfUrl: "https://www.irs.gov/pub/irs-pdf/f1040sa.pdf",
      condition: (p) => p.has_itemized_deductions || p.has_home_mortgage || p.has_charitable_donations
    },
    "ScheduleB": {
      name: "Schedule B",
      title: "Interest and Ordinary Dividends",
      description: "Required if you have over $1,500 in interest or dividends",
      pdfUrl: "https://www.irs.gov/pub/irs-pdf/f1040sb.pdf",
      condition: (p) => p.has_investments
    },
    "ScheduleC": {
      name: "Schedule C",
      title: "Profit or Loss from Business",
      description: "Self-employment income, business expenses, home office deduction",
      pdfUrl: "https://www.irs.gov/pub/irs-pdf/f1040sc.pdf",
      condition: (p) => p.has_self_employment
    },
    "ScheduleD": {
      name: "Schedule D",
      title: "Capital Gains and Losses",
      description: "Stock sales, crypto transactions, investment property sales",
      pdfUrl: "https://www.irs.gov/pub/irs-pdf/f1040sd.pdf",
      condition: (p) => p.has_investments
    },
    "ScheduleE": {
      name: "Schedule E",
      title: "Supplemental Income and Loss",
      description: "Rental property income/expenses, royalties, partnerships, S-corps",
      pdfUrl: "https://www.irs.gov/pub/irs-pdf/f1040se.pdf",
      condition: (p) => p.has_rental_property
    },
    "ScheduleSE": {
      name: "Schedule SE",
      title: "Self-Employment Tax",
      description: "Calculate self-employment tax (Social Security & Medicare) on business income",
      pdfUrl: "https://www.irs.gov/pub/irs-pdf/f1040sse.pdf",
      condition: (p) => p.has_self_employment
    },
    "Schedule8812": {
      name: "Schedule 8812",
      title: "Credits for Qualifying Children",
      description: "Child Tax Credit and Additional Child Tax Credit",
      pdfUrl: "https://www.irs.gov/pub/irs-pdf/f1040s8.pdf",
      condition: (p) => p.has_dependents && p.num_dependents > 0
    },
    "Form8889": {
      name: "Form 8889",
      title: "Health Savings Accounts (HSAs)",
      description: "Report HSA contributions and distributions",
      pdfUrl: "https://www.irs.gov/pub/irs-pdf/f8889.pdf",
      condition: (p) => p.has_health_savings
    },
    "Form8863": {
      name: "Form 8863",
      title: "Education Credits",
      description: "American Opportunity Credit and Lifetime Learning Credit",
      pdfUrl: "https://www.irs.gov/pub/irs-pdf/f8863.pdf",
      condition: (p) => p.has_education_expenses
    }
  };

  // Determine required forms based on user profile
  const determineRequiredForms = () => {
    const forms = [];
    Object.entries(irsFormsDatabase).forEach(([key, form]) => {
      if (form.everyone || (form.condition && form.condition(userProfile))) {
        forms.push({ key, ...form });
      }
    });
    setRequiredForms(forms);
    setFormAssistantStep(2);
  };

  // Fetch user's financial data and generate form data
  const fetchAndGenerateFormData = async () => {
    setFormLoading(true);
    try {
      // Fetch all user's financial data from WealthHub
      const [incomeRes, expensesRes, propertiesRes, investmentsRes, businessRes, loansRes] = await Promise.all([
        api.get("/income").catch(() => ({ data: [] })),
        api.get("/expenses").catch(() => ({ data: [] })),
        api.get("/properties").catch(() => ({ data: [] })),
        api.get("/investments").catch(() => ({ data: [] })),
        api.get("/businesses").catch(() => ({ data: [] })),
        api.get("/loans").catch(() => ({ data: [] }))
      ]);

      const userData = {
        income: incomeRes.data || [],
        expenses: expensesRes.data || [],
        properties: propertiesRes.data || [],
        investments: investmentsRes.data || [],
        businesses: businessRes.data || [],
        loans: loansRes.data || []
      };

      setFormData(userData);

      // Call AI to fill out the forms
      const res = await api.post("/ai/fill-tax-forms", {
        user_profile: userProfile,
        required_forms: requiredForms.map(f => f.key),
        financial_data: userData
      });

      setFilledForms(res.data.filled_forms);
      setFormAssistantStep(3);
      toast.success("Forms generated successfully!");
    } catch (e) {
      console.error("Form generation error:", e);
      toast.error("Failed to generate form data");
    } finally {
      setFormLoading(false);
    }
  };

  const resetFormAssistant = () => {
    setFormAssistantStep(1);
    setRequiredForms(null);
    setFormData(null);
    setFilledForms(null);
  };

  // ==================== TAX DOCUMENTS (W-2, 1099) ====================
  const [taxDocSummary, setTaxDocSummary] = useState(null);
  const [w2Forms, setW2Forms] = useState([]);
  const [form1099s, setForm1099s] = useState([]);
  const [showW2Form, setShowW2Form] = useState(false);
  const [show1099Form, setShow1099Form] = useState(false);
  const [editingW2, setEditingW2] = useState(null);
  const [editing1099, setEditing1099] = useState(null);
  
  const [w2Data, setW2Data] = useState({
    tax_year: 2025,
    employer_name: "",
    employer_ein: "",
    box_1_wages: "",
    box_2_federal_withheld: "",
    box_3_ss_wages: "",
    box_4_ss_withheld: "",
    box_5_medicare_wages: "",
    box_6_medicare_withheld: "",
    box_12a_code: "",
    box_12a_amount: "",
    box_15_state: "",
    box_16_state_wages: "",
    box_17_state_tax: ""
  });

  const [form1099Data, setForm1099Data] = useState({
    tax_year: 2025,
    form_type: "1099-DIV",
    payer_name: "",
    // DIV fields
    div_ordinary: "",
    div_qualified: "",
    div_capital_gain: "",
    // INT fields
    int_income: "",
    int_federal_withheld: "",
    // B fields (brokerage)
    proceeds: "",
    cost_basis: "",
    short_term: true,
    description: "",
    date_acquired: "",
    date_sold: "",
    // NEC fields
    nec_compensation: "",
    // MISC fields
    misc_rents: "",
    misc_royalties: "",
    misc_other_income: ""
  });

  // Load tax documents
  useEffect(() => {
    loadTaxDocuments();
  }, []);

  const loadTaxDocuments = async () => {
    try {
      const [w2Res, f1099Res, summaryRes] = await Promise.all([
        api.get("/tax-documents/w2?tax_year=2025"),
        api.get("/tax-documents/1099?tax_year=2025"),
        api.get("/tax-documents/summary?tax_year=2025")
      ]);
      setW2Forms(w2Res.data || []);
      setForm1099s(f1099Res.data || []);
      setTaxDocSummary(summaryRes.data);
    } catch (e) {
      console.error("Error loading tax docs:", e);
    }
  };

  const handleW2Submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...w2Data,
        box_1_wages: parseFloat(w2Data.box_1_wages) || 0,
        box_2_federal_withheld: parseFloat(w2Data.box_2_federal_withheld) || 0,
        box_3_ss_wages: parseFloat(w2Data.box_3_ss_wages) || 0,
        box_4_ss_withheld: parseFloat(w2Data.box_4_ss_withheld) || 0,
        box_5_medicare_wages: parseFloat(w2Data.box_5_medicare_wages) || 0,
        box_6_medicare_withheld: parseFloat(w2Data.box_6_medicare_withheld) || 0,
        box_12a_amount: parseFloat(w2Data.box_12a_amount) || 0,
        box_16_state_wages: parseFloat(w2Data.box_16_state_wages) || 0,
        box_17_state_tax: parseFloat(w2Data.box_17_state_tax) || 0
      };

      if (editingW2) {
        await api.put(`/tax-documents/w2/${editingW2}`, payload);
        toast.success("W-2 updated");
      } else {
        await api.post("/tax-documents/w2", payload);
        toast.success("W-2 added");
      }
      
      resetW2Form();
      loadTaxDocuments();
    } catch (e) {
      toast.error("Failed to save W-2");
    } finally {
      setLoading(false);
    }
  };

  const handle1099Submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form1099Data,
        div_ordinary: parseFloat(form1099Data.div_ordinary) || 0,
        div_qualified: parseFloat(form1099Data.div_qualified) || 0,
        div_capital_gain: parseFloat(form1099Data.div_capital_gain) || 0,
        int_income: parseFloat(form1099Data.int_income) || 0,
        int_federal_withheld: parseFloat(form1099Data.int_federal_withheld) || 0,
        proceeds: parseFloat(form1099Data.proceeds) || 0,
        cost_basis: parseFloat(form1099Data.cost_basis) || 0,
        nec_compensation: parseFloat(form1099Data.nec_compensation) || 0,
        misc_rents: parseFloat(form1099Data.misc_rents) || 0,
        misc_royalties: parseFloat(form1099Data.misc_royalties) || 0,
        misc_other_income: parseFloat(form1099Data.misc_other_income) || 0
      };

      if (editing1099) {
        await api.put(`/tax-documents/1099/${editing1099}`, payload);
        toast.success("1099 updated");
      } else {
        await api.post("/tax-documents/1099", payload);
        toast.success("1099 added");
      }
      
      reset1099Form();
      loadTaxDocuments();
    } catch (e) {
      toast.error("Failed to save 1099");
    } finally {
      setLoading(false);
    }
  };

  const resetW2Form = () => {
    setW2Data({
      tax_year: 2025,
      employer_name: "",
      employer_ein: "",
      box_1_wages: "",
      box_2_federal_withheld: "",
      box_3_ss_wages: "",
      box_4_ss_withheld: "",
      box_5_medicare_wages: "",
      box_6_medicare_withheld: "",
      box_12a_code: "",
      box_12a_amount: "",
      box_15_state: "",
      box_16_state_wages: "",
      box_17_state_tax: ""
    });
    setShowW2Form(false);
    setEditingW2(null);
  };

  const reset1099Form = () => {
    setForm1099Data({
      tax_year: 2025,
      form_type: "1099-DIV",
      payer_name: "",
      div_ordinary: "",
      div_qualified: "",
      div_capital_gain: "",
      int_income: "",
      int_federal_withheld: "",
      proceeds: "",
      cost_basis: "",
      short_term: true,
      description: "",
      date_acquired: "",
      date_sold: "",
      nec_compensation: "",
      misc_rents: "",
      misc_royalties: "",
      misc_other_income: ""
    });
    setShow1099Form(false);
    setEditing1099(null);
  };

  const editW2 = (w2) => {
    setW2Data({
      tax_year: w2.tax_year,
      employer_name: w2.employer_name,
      employer_ein: w2.employer_ein || "",
      box_1_wages: w2.box_1_wages?.toString() || "",
      box_2_federal_withheld: w2.box_2_federal_withheld?.toString() || "",
      box_3_ss_wages: w2.box_3_ss_wages?.toString() || "",
      box_4_ss_withheld: w2.box_4_ss_withheld?.toString() || "",
      box_5_medicare_wages: w2.box_5_medicare_wages?.toString() || "",
      box_6_medicare_withheld: w2.box_6_medicare_withheld?.toString() || "",
      box_12a_code: w2.box_12a_code || "",
      box_12a_amount: w2.box_12a_amount?.toString() || "",
      box_15_state: w2.box_15_state || "",
      box_16_state_wages: w2.box_16_state_wages?.toString() || "",
      box_17_state_tax: w2.box_17_state_tax?.toString() || ""
    });
    setEditingW2(w2.id);
    setShowW2Form(true);
  };

  const edit1099 = (f) => {
    setForm1099Data({
      tax_year: f.tax_year,
      form_type: f.form_type,
      payer_name: f.payer_name,
      div_ordinary: f.div_ordinary?.toString() || "",
      div_qualified: f.div_qualified?.toString() || "",
      div_capital_gain: f.div_capital_gain?.toString() || "",
      int_income: f.int_income?.toString() || "",
      int_federal_withheld: f.int_federal_withheld?.toString() || "",
      proceeds: f.proceeds?.toString() || "",
      cost_basis: f.cost_basis?.toString() || "",
      short_term: f.short_term,
      description: f.description || "",
      date_acquired: f.date_acquired || "",
      date_sold: f.date_sold || "",
      nec_compensation: f.nec_compensation?.toString() || "",
      misc_rents: f.misc_rents?.toString() || "",
      misc_royalties: f.misc_royalties?.toString() || "",
      misc_other_income: f.misc_other_income?.toString() || ""
    });
    setEditing1099(f.id);
    setShow1099Form(true);
  };

  const deleteW2 = async (id) => {
    if (!confirm("Delete this W-2?")) return;
    try {
      await api.delete(`/tax-documents/w2/${id}`);
      toast.success("W-2 deleted");
      loadTaxDocuments();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const delete1099 = async (id) => {
    if (!confirm("Delete this 1099?")) return;
    try {
      await api.delete(`/tax-documents/1099/${id}`);
      toast.success("1099 deleted");
      loadTaxDocuments();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  // Download filled IRS PDF
  const downloadFilledPDF = async (formKey) => {
    setFormLoading(true);
    try {
      const res = await api.post("/ai/generate-tax-pdf", {
        user_profile: userProfile,
        financial_data: formData,
        forms_to_generate: [formKey]
      });
      
      if (res.data.pdfs && res.data.pdfs[formKey]) {
        const pdfData = res.data.pdfs[formKey];
        
        // Convert base64 to blob
        const byteCharacters = atob(pdfData.base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = pdfData.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success(`${formKey} PDF downloaded successfully!`);
      }
    } catch (e) {
      console.error("PDF download error:", e);
      toast.error("Failed to generate PDF. Please try again.");
    } finally {
      setFormLoading(false);
    }
  };

  const handleTaxLiabilityEstimate = async () => {
    setLiabilityLoading(true);
    setLiabilityResult(null);
    try {
      const res = await api.post("/tax-documents/estimate-liability", {
        tax_year: liabilityForm.tax_year,
        filing_status: liabilityForm.filing_status,
        additional_deductions: parseFloat(liabilityForm.additional_deductions) || 0,
        state: liabilityForm.state
      });
      setLiabilityResult(res.data);
    } catch (e) {
      toast.error("Failed to calculate tax liability");
    } finally {
      setLiabilityLoading(false);
    }
  };

  const downloadTaxReport = () => {
    if (!liabilityResult) return;
    const r = liabilityResult;
    const lines = [
      `WEALTHHUB TAX LIABILITY ESTIMATE - ${r.tax_year}`,
      `Generated: ${new Date().toLocaleString()}`,
      `Filing Status: ${r.filing_status.replace('_', ' ').toUpperCase()}`,
      `${r.state_tax ? `State: ${r.state_tax.state_name}` : 'Federal Only'}`,
      '',
      '=== INCOME SOURCES ===',
      ...Object.entries(r.income_sources).filter(([,v]) => v > 0).map(([k,v]) => `  ${k.replace(/_/g,' ')}: $${v.toLocaleString('en-US', {minimumFractionDigits: 2})}`),
      '',
      '=== CALCULATIONS ===',
      `  Gross Income: $${r.calculations.gross_income.toLocaleString('en-US', {minimumFractionDigits: 2})}`,
      `  Adjustments: $${r.calculations.adjustments.toLocaleString('en-US', {minimumFractionDigits: 2})}`,
      `  AGI: $${r.calculations.agi.toLocaleString('en-US', {minimumFractionDigits: 2})}`,
      `  ${r.calculations.using_standard_deduction ? 'Standard' : 'Itemized'} Deduction: $${r.calculations.deduction_amount.toLocaleString('en-US', {minimumFractionDigits: 2})}`,
      `  Taxable Income: $${r.calculations.taxable_income.toLocaleString('en-US', {minimumFractionDigits: 2})}`,
      '',
      '=== FEDERAL TAX BRACKETS ===',
      ...r.tax_breakdown.bracket_breakdown.map(b => `  ${(b.rate*100).toFixed(0)}% on $${b.income_in_bracket.toLocaleString('en-US', {minimumFractionDigits: 2})} = $${b.tax.toLocaleString('en-US', {minimumFractionDigits: 2})}`),
      '',
      '=== FEDERAL TAX SUMMARY ===',
      `  Ordinary Income Tax: $${r.tax_breakdown.ordinary_income_tax.toLocaleString('en-US', {minimumFractionDigits: 2})}`,
      `  Capital Gains Tax: $${r.tax_breakdown.preferential_tax.toLocaleString('en-US', {minimumFractionDigits: 2})}`,
      r.tax_breakdown.self_employment_tax > 0 ? `  Self-Employment Tax: $${r.tax_breakdown.self_employment_tax.toLocaleString('en-US', {minimumFractionDigits: 2})}` : null,
      r.tax_breakdown.niit > 0 ? `  NIIT: $${r.tax_breakdown.niit.toLocaleString('en-US', {minimumFractionDigits: 2})}` : null,
      `  TOTAL FEDERAL TAX: $${r.tax_breakdown.total_tax.toLocaleString('en-US', {minimumFractionDigits: 2})}`,
      '',
      '=== WITHHOLDING ===',
      `  W-2 Federal Withheld: $${r.withholding.w2_federal.toLocaleString('en-US', {minimumFractionDigits: 2})}`,
      r.withholding.from_1099 > 0 ? `  1099 Withholding: $${r.withholding.from_1099.toLocaleString('en-US', {minimumFractionDigits: 2})}` : null,
      `  Total Federal Withheld: $${r.withholding.total_federal_withheld.toLocaleString('en-US', {minimumFractionDigits: 2})}`,
    ].filter(Boolean);
    
    if (r.state_tax) {
      lines.push('', `=== ${r.state_tax.state_name.toUpperCase()} STATE TAX ===`);
      lines.push(`  Taxable Income: $${r.state_tax.taxable_income.toLocaleString('en-US', {minimumFractionDigits: 2})}`);
      r.state_tax.bracket_breakdown.forEach(b => {
        lines.push(`  ${(b.rate*100).toFixed(2)}% on $${b.income_in_bracket.toLocaleString('en-US', {minimumFractionDigits: 2})} = $${b.tax.toLocaleString('en-US', {minimumFractionDigits: 2})}`);
      });
      lines.push(`  Total State Tax: $${r.state_tax.state_tax.toLocaleString('en-US', {minimumFractionDigits: 2})}`);
      lines.push(`  State Withheld: $${r.state_tax.state_withheld.toLocaleString('en-US', {minimumFractionDigits: 2})}`);
      lines.push(`  State ${r.state_tax.state_is_refund ? 'REFUND' : 'OWED'}: $${Math.abs(r.state_tax.state_balance).toLocaleString('en-US', {minimumFractionDigits: 2})}`);
    }
    
    lines.push('', '=== FINAL RESULT ===');
    lines.push(`  Federal ${r.result.is_refund ? 'REFUND' : 'OWED'}: $${Math.abs(r.result.balance).toLocaleString('en-US', {minimumFractionDigits: 2})}`);
    if (r.state_tax) {
      lines.push(`  Combined ${r.result.combined_is_refund ? 'REFUND' : 'OWED'}: $${Math.abs(r.result.combined_balance).toLocaleString('en-US', {minimumFractionDigits: 2})}`);
    }
    lines.push(`  Effective Rate: ${r.result.effective_rate}%`);
    lines.push(`  Marginal Rate: ${r.result.marginal_rate}%`);
    lines.push('', 'DISCLAIMER: This is an estimate. Consult a tax professional for accurate filing.');
    
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `WealthHub_Tax_Estimate_${r.tax_year}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Tax report downloaded");
  };

  const handleTaxEstimate = async () => {
    if (!taxForm.total_income) {
      toast.error("Please enter your total income");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post("/ai/tax-estimate", {
        ...taxForm,
        total_income: parseFloat(taxForm.total_income) || 0,
        business_income: parseFloat(taxForm.business_income) || 0,
        deductions: parseFloat(taxForm.deductions) || 0
      });
      setResult(res.data.analysis);
    } catch (e) {
      toast.error("Failed to get tax estimate");
    } finally {
      setLoading(false);
    }
  };

  const handleIrsSearch = async () => {
    if (!irsQuery.trim()) {
      toast.error("Please enter a search query");
      return;
    }
    setLoading(true);
    setIrsResult(null);
    try {
      const res = await api.post(`/ai/irs-search?query=${encodeURIComponent(irsQuery)}`);
      setIrsResult(res.data.response);
    } catch (e) {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const handlePricingAnalysis = async () => {
    if (!pricingForm.business_type || !pricingForm.product_service || !pricingForm.costs) {
      toast.error("Please fill all fields");
      return;
    }
    setLoading(true);
    setPricingResult(null);
    try {
      const res = await api.post(`/ai/business-pricing?business_type=${encodeURIComponent(pricingForm.business_type)}&product_service=${encodeURIComponent(pricingForm.product_service)}&costs=${parseFloat(pricingForm.costs)}`);
      setPricingResult(res.data.analysis);
    } catch (e) {
      toast.error("Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title="Tax Center">
      <div className="space-y-6">
        {/* Header */}
        <Card className="bg-gradient-to-r from-violet-500/20 to-amber-500/20 border-white/10 rounded-none">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded bg-violet-500/30 flex items-center justify-center">
                <Calculator className="w-8 h-8 text-violet-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">AI-Powered Tax Center</h2>
                <p className="text-zinc-400">Tax estimates, IRS guidance, and business pricing powered by GPT-5.2</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="documents" className="w-full">
          <TabsList className="bg-[#0A0A0A] border border-white/10 rounded-none flex-wrap">
            <TabsTrigger value="documents" className="rounded-none data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
              <Receipt className="w-4 h-4 mr-2" /> W-2 & 1099 Forms
            </TabsTrigger>
            <TabsTrigger value="forms" className="rounded-none data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400">
              <ClipboardList className="w-4 h-4 mr-2" /> AI Form Assistant
            </TabsTrigger>
            <TabsTrigger value="education" className="rounded-none data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400">
              <GraduationCap className="w-4 h-4 mr-2" /> 2026 Tax Education
            </TabsTrigger>
            <TabsTrigger value="estimate" className="rounded-none data-[state=active]:bg-white/10">
              <DollarSign className="w-4 h-4 mr-2" /> Tax Estimate
            </TabsTrigger>
            <TabsTrigger value="irs" className="rounded-none data-[state=active]:bg-white/10">
              <Search className="w-4 h-4 mr-2" /> IRS Search
            </TabsTrigger>
          </TabsList>

          {/* W-2 & 1099 Tax Documents Tab */}
          <TabsContent value="documents" className="mt-4 space-y-6">
            {/* Summary Cards */}
            {taxDocSummary && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border-emerald-500/20 rounded-none">
                  <CardContent className="p-4">
                    <p className="text-xs text-emerald-400 uppercase">Total W-2 Wages</p>
                    <p className="text-2xl font-bold text-white font-mono">{formatCurrency(taxDocSummary.w2_summary?.total_wages || 0)}</p>
                    <p className="text-xs text-zinc-500 mt-1">{taxDocSummary.w2_summary?.count || 0} W-2 form(s)</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20 rounded-none">
                  <CardContent className="p-4">
                    <p className="text-xs text-blue-400 uppercase">Investment Income</p>
                    <p className="text-2xl font-bold text-white font-mono">{formatCurrency((taxDocSummary["1099_summary"]?.total_dividends || 0) + (taxDocSummary["1099_summary"]?.total_interest || 0))}</p>
                    <p className="text-xs text-zinc-500 mt-1">Dividends + Interest</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-violet-500/10 to-violet-500/5 border-violet-500/20 rounded-none">
                  <CardContent className="p-4">
                    <p className="text-xs text-violet-400 uppercase">Capital Gains/Losses</p>
                    <p className={`text-2xl font-bold font-mono ${(taxDocSummary["1099_summary"]?.total_gain_loss || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {formatCurrency(taxDocSummary["1099_summary"]?.total_gain_loss || 0)}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">{taxDocSummary["1099_summary"]?.b_count || 0} transactions</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border-amber-500/20 rounded-none">
                  <CardContent className="p-4">
                    <p className="text-xs text-amber-400 uppercase">Federal Withheld</p>
                    <p className="text-2xl font-bold text-white font-mono">{formatCurrency(taxDocSummary.totals?.total_federal_withheld || 0)}</p>
                    <p className="text-xs text-zinc-500 mt-1">Payments to IRS</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* W-2 Section */}
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-emerald-400" /> W-2 Forms (Employment Income)
                  </span>
                  <Button onClick={() => { resetW2Form(); setShowW2Form(true); }} size="sm" className="bg-emerald-500 text-black hover:bg-emerald-600 rounded-sm">
                    <Plus className="w-4 h-4 mr-1" /> Add W-2
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {showW2Form && (
                  <form onSubmit={handleW2Submit} className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-sm mb-4 space-y-4">
                    <h4 className="text-sm font-medium text-emerald-400">{editingW2 ? "Edit W-2" : "Add New W-2"}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-zinc-400 text-xs">Employer Name *</Label>
                        <Input value={w2Data.employer_name} onChange={(e) => setW2Data({...w2Data, employer_name: e.target.value})} required className="bg-black/50 border-white/10 rounded-sm mt-1" placeholder="Company Inc." />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Employer EIN</Label>
                        <Input value={w2Data.employer_ein} onChange={(e) => setW2Data({...w2Data, employer_ein: e.target.value})} className="bg-black/50 border-white/10 rounded-sm mt-1" placeholder="XX-XXXXXXX" />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Tax Year</Label>
                        <Select value={w2Data.tax_year.toString()} onValueChange={(v) => setW2Data({...w2Data, tax_year: parseInt(v)})}>
                          <SelectTrigger className="bg-black/50 border-white/10 rounded-sm mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A0A] border-white/10">
                            <SelectItem value="2025">2025</SelectItem>
                            <SelectItem value="2024">2024</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <Label className="text-zinc-400 text-xs">Box 1: Wages *</Label>
                        <Input type="number" step="0.01" value={w2Data.box_1_wages} onChange={(e) => setW2Data({...w2Data, box_1_wages: e.target.value})} required className="bg-black/50 border-white/10 rounded-sm mt-1 font-mono" placeholder="0.00" />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Box 2: Fed Withheld</Label>
                        <Input type="number" step="0.01" value={w2Data.box_2_federal_withheld} onChange={(e) => setW2Data({...w2Data, box_2_federal_withheld: e.target.value})} className="bg-black/50 border-white/10 rounded-sm mt-1 font-mono" placeholder="0.00" />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Box 4: SS Withheld</Label>
                        <Input type="number" step="0.01" value={w2Data.box_4_ss_withheld} onChange={(e) => setW2Data({...w2Data, box_4_ss_withheld: e.target.value})} className="bg-black/50 border-white/10 rounded-sm mt-1 font-mono" placeholder="0.00" />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Box 6: Medicare Withheld</Label>
                        <Input type="number" step="0.01" value={w2Data.box_6_medicare_withheld} onChange={(e) => setW2Data({...w2Data, box_6_medicare_withheld: e.target.value})} className="bg-black/50 border-white/10 rounded-sm mt-1 font-mono" placeholder="0.00" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <Label className="text-zinc-400 text-xs">Box 12a Code</Label>
                        <Select value={w2Data.box_12a_code || undefined} onValueChange={(v) => setW2Data({...w2Data, box_12a_code: v === "none" ? "" : v})}>
                          <SelectTrigger className="bg-black/50 border-white/10 rounded-sm mt-1">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A0A] border-white/10">
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="D">D - 401(k)</SelectItem>
                            <SelectItem value="E">E - 403(b)</SelectItem>
                            <SelectItem value="DD">DD - Health Coverage</SelectItem>
                            <SelectItem value="W">W - HSA Contributions</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Box 12a Amount</Label>
                        <Input type="number" step="0.01" value={w2Data.box_12a_amount} onChange={(e) => setW2Data({...w2Data, box_12a_amount: e.target.value})} className="bg-black/50 border-white/10 rounded-sm mt-1 font-mono" placeholder="0.00" />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Box 15: State</Label>
                        <Input value={w2Data.box_15_state} onChange={(e) => setW2Data({...w2Data, box_15_state: e.target.value})} className="bg-black/50 border-white/10 rounded-sm mt-1" placeholder="CA" maxLength={2} />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Box 17: State Tax</Label>
                        <Input type="number" step="0.01" value={w2Data.box_17_state_tax} onChange={(e) => setW2Data({...w2Data, box_17_state_tax: e.target.value})} className="bg-black/50 border-white/10 rounded-sm mt-1 font-mono" placeholder="0.00" />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" disabled={loading} className="bg-emerald-500 text-black hover:bg-emerald-600 rounded-sm">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingW2 ? "Update W-2" : "Save W-2")}
                      </Button>
                      <Button type="button" onClick={resetW2Form} variant="outline" className="border-white/20 text-zinc-400 rounded-sm">Cancel</Button>
                    </div>
                  </form>
                )}
                
                {w2Forms.length > 0 ? (
                  <div className="space-y-2">
                    {w2Forms.map((w2) => (
                      <div key={w2.id} className="flex items-center justify-between p-3 bg-white/5 border border-white/10 rounded-sm">
                        <div className="flex-1">
                          <p className="text-white font-medium">{w2.employer_name}</p>
                          <p className="text-xs text-zinc-500">EIN: {w2.employer_ein || "N/A"} | Tax Year: {w2.tax_year}</p>
                        </div>
                        <div className="text-right mr-4">
                          <p className="text-emerald-400 font-mono font-bold">{formatCurrency(w2.box_1_wages)}</p>
                          <p className="text-xs text-zinc-500">Fed Withheld: {formatCurrency(w2.box_2_federal_withheld)}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={() => editW2(w2)} size="sm" variant="ghost" className="text-zinc-400 hover:text-white"><Edit className="w-4 h-4" /></Button>
                          <Button onClick={() => deleteW2(w2.id)} size="sm" variant="ghost" className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-zinc-500 text-sm text-center py-8">No W-2 forms added yet. Click "Add W-2" to enter your employment income.</p>
                )}
              </CardContent>
            </Card>

            {/* 1099 Section */}
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-400" /> 1099 Forms (Investment & Other Income)
                  </span>
                  <Button onClick={() => { reset1099Form(); setShow1099Form(true); }} size="sm" className="bg-blue-500 text-white hover:bg-blue-600 rounded-sm">
                    <Plus className="w-4 h-4 mr-1" /> Add 1099
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {show1099Form && (
                  <form onSubmit={handle1099Submit} className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-sm mb-4 space-y-4">
                    <h4 className="text-sm font-medium text-blue-400">{editing1099 ? "Edit 1099" : "Add New 1099"}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-zinc-400 text-xs">Form Type *</Label>
                        <Select value={form1099Data.form_type} onValueChange={(v) => setForm1099Data({...form1099Data, form_type: v})}>
                          <SelectTrigger className="bg-black/50 border-white/10 rounded-sm mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A0A] border-white/10">
                            <SelectItem value="1099-DIV">1099-DIV (Dividends)</SelectItem>
                            <SelectItem value="1099-INT">1099-INT (Interest)</SelectItem>
                            <SelectItem value="1099-B">1099-B (Brokerage/Sales)</SelectItem>
                            <SelectItem value="1099-NEC">1099-NEC (Freelance/Contract)</SelectItem>
                            <SelectItem value="1099-MISC">1099-MISC (Miscellaneous)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Payer Name *</Label>
                        <Input value={form1099Data.payer_name} onChange={(e) => setForm1099Data({...form1099Data, payer_name: e.target.value})} required className="bg-black/50 border-white/10 rounded-sm mt-1" placeholder="Fidelity, Schwab, etc." />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Tax Year</Label>
                        <Select value={form1099Data.tax_year.toString()} onValueChange={(v) => setForm1099Data({...form1099Data, tax_year: parseInt(v)})}>
                          <SelectTrigger className="bg-black/50 border-white/10 rounded-sm mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A0A] border-white/10">
                            <SelectItem value="2025">2025</SelectItem>
                            <SelectItem value="2024">2024</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* 1099-DIV Fields */}
                    {form1099Data.form_type === "1099-DIV" && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-zinc-400 text-xs">Box 1a: Total Dividends</Label>
                          <Input type="number" step="0.01" value={form1099Data.div_ordinary} onChange={(e) => setForm1099Data({...form1099Data, div_ordinary: e.target.value})} className="bg-black/50 border-white/10 rounded-sm mt-1 font-mono" placeholder="0.00" />
                        </div>
                        <div>
                          <Label className="text-zinc-400 text-xs">Box 1b: Qualified Dividends</Label>
                          <Input type="number" step="0.01" value={form1099Data.div_qualified} onChange={(e) => setForm1099Data({...form1099Data, div_qualified: e.target.value})} className="bg-black/50 border-white/10 rounded-sm mt-1 font-mono" placeholder="0.00" />
                        </div>
                        <div>
                          <Label className="text-zinc-400 text-xs">Box 2a: Capital Gain Dist</Label>
                          <Input type="number" step="0.01" value={form1099Data.div_capital_gain} onChange={(e) => setForm1099Data({...form1099Data, div_capital_gain: e.target.value})} className="bg-black/50 border-white/10 rounded-sm mt-1 font-mono" placeholder="0.00" />
                        </div>
                      </div>
                    )}

                    {/* 1099-INT Fields */}
                    {form1099Data.form_type === "1099-INT" && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-zinc-400 text-xs">Box 1: Interest Income</Label>
                          <Input type="number" step="0.01" value={form1099Data.int_income} onChange={(e) => setForm1099Data({...form1099Data, int_income: e.target.value})} className="bg-black/50 border-white/10 rounded-sm mt-1 font-mono" placeholder="0.00" />
                        </div>
                        <div>
                          <Label className="text-zinc-400 text-xs">Box 4: Fed Tax Withheld</Label>
                          <Input type="number" step="0.01" value={form1099Data.int_federal_withheld} onChange={(e) => setForm1099Data({...form1099Data, int_federal_withheld: e.target.value})} className="bg-black/50 border-white/10 rounded-sm mt-1 font-mono" placeholder="0.00" />
                        </div>
                      </div>
                    )}

                    {/* 1099-B Fields */}
                    {form1099Data.form_type === "1099-B" && (
                      <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <Label className="text-zinc-400 text-xs">Description</Label>
                            <Input value={form1099Data.description} onChange={(e) => setForm1099Data({...form1099Data, description: e.target.value})} className="bg-black/50 border-white/10 rounded-sm mt-1" placeholder="AAPL, VTI, etc." />
                          </div>
                          <div>
                            <Label className="text-zinc-400 text-xs">Proceeds (1d)</Label>
                            <Input type="number" step="0.01" value={form1099Data.proceeds} onChange={(e) => setForm1099Data({...form1099Data, proceeds: e.target.value})} className="bg-black/50 border-white/10 rounded-sm mt-1 font-mono" placeholder="0.00" />
                          </div>
                          <div>
                            <Label className="text-zinc-400 text-xs">Cost Basis (1e)</Label>
                            <Input type="number" step="0.01" value={form1099Data.cost_basis} onChange={(e) => setForm1099Data({...form1099Data, cost_basis: e.target.value})} className="bg-black/50 border-white/10 rounded-sm mt-1 font-mono" placeholder="0.00" />
                          </div>
                          <div>
                            <Label className="text-zinc-400 text-xs">Term</Label>
                            <Select value={form1099Data.short_term ? "short" : "long"} onValueChange={(v) => setForm1099Data({...form1099Data, short_term: v === "short"})}>
                              <SelectTrigger className="bg-black/50 border-white/10 rounded-sm mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#0A0A0A] border-white/10">
                                <SelectItem value="short">Short-term (&lt;1 yr)</SelectItem>
                                <SelectItem value="long">Long-term (&gt;1 yr)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-zinc-400 text-xs">Date Acquired</Label>
                            <Input type="date" value={form1099Data.date_acquired} onChange={(e) => setForm1099Data({...form1099Data, date_acquired: e.target.value})} className="bg-black/50 border-white/10 rounded-sm mt-1" />
                          </div>
                          <div>
                            <Label className="text-zinc-400 text-xs">Date Sold</Label>
                            <Input type="date" value={form1099Data.date_sold} onChange={(e) => setForm1099Data({...form1099Data, date_sold: e.target.value})} className="bg-black/50 border-white/10 rounded-sm mt-1" />
                          </div>
                        </div>
                      </>
                    )}

                    {/* 1099-NEC Fields */}
                    {form1099Data.form_type === "1099-NEC" && (
                      <div>
                        <Label className="text-zinc-400 text-xs">Box 1: Nonemployee Compensation</Label>
                        <Input type="number" step="0.01" value={form1099Data.nec_compensation} onChange={(e) => setForm1099Data({...form1099Data, nec_compensation: e.target.value})} className="bg-black/50 border-white/10 rounded-sm mt-1 font-mono" placeholder="0.00" />
                      </div>
                    )}

                    {/* 1099-MISC Fields */}
                    {form1099Data.form_type === "1099-MISC" && (
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-zinc-400 text-xs">Box 1: Rents</Label>
                          <Input type="number" step="0.01" value={form1099Data.misc_rents} onChange={(e) => setForm1099Data({...form1099Data, misc_rents: e.target.value})} className="bg-black/50 border-white/10 rounded-sm mt-1 font-mono" placeholder="0.00" />
                        </div>
                        <div>
                          <Label className="text-zinc-400 text-xs">Box 2: Royalties</Label>
                          <Input type="number" step="0.01" value={form1099Data.misc_royalties} onChange={(e) => setForm1099Data({...form1099Data, misc_royalties: e.target.value})} className="bg-black/50 border-white/10 rounded-sm mt-1 font-mono" placeholder="0.00" />
                        </div>
                        <div>
                          <Label className="text-zinc-400 text-xs">Box 3: Other Income</Label>
                          <Input type="number" step="0.01" value={form1099Data.misc_other_income} onChange={(e) => setForm1099Data({...form1099Data, misc_other_income: e.target.value})} className="bg-black/50 border-white/10 rounded-sm mt-1 font-mono" placeholder="0.00" />
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button type="submit" disabled={loading} className="bg-blue-500 text-white hover:bg-blue-600 rounded-sm">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing1099 ? "Update 1099" : "Save 1099")}
                      </Button>
                      <Button type="button" onClick={reset1099Form} variant="outline" className="border-white/20 text-zinc-400 rounded-sm">Cancel</Button>
                    </div>
                  </form>
                )}
                
                {form1099s.length > 0 ? (
                  <div className="space-y-2">
                    {form1099s.map((f) => (
                      <div key={f.id} className="flex items-center justify-between p-3 bg-white/5 border border-white/10 rounded-sm">
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            f.form_type === "1099-DIV" ? "bg-purple-500/20 text-purple-400" :
                            f.form_type === "1099-INT" ? "bg-cyan-500/20 text-cyan-400" :
                            f.form_type === "1099-B" ? "bg-amber-500/20 text-amber-400" :
                            f.form_type === "1099-NEC" ? "bg-rose-500/20 text-rose-400" :
                            "bg-zinc-500/20 text-zinc-400"
                          }`}>{f.form_type}</span>
                          <div>
                            <p className="text-white font-medium">{f.payer_name}</p>
                            <p className="text-xs text-zinc-500">{f.description || `Tax Year: ${f.tax_year}`}</p>
                          </div>
                        </div>
                        <div className="text-right mr-4">
                          {f.form_type === "1099-DIV" && <p className="text-purple-400 font-mono">{formatCurrency(f.div_ordinary)}</p>}
                          {f.form_type === "1099-INT" && <p className="text-cyan-400 font-mono">{formatCurrency(f.int_income)}</p>}
                          {f.form_type === "1099-B" && (
                            <p className={`font-mono ${f.gain_loss >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {formatCurrency(f.gain_loss)} {f.short_term ? "(ST)" : "(LT)"}
                            </p>
                          )}
                          {f.form_type === "1099-NEC" && <p className="text-rose-400 font-mono">{formatCurrency(f.nec_compensation)}</p>}
                          {f.form_type === "1099-MISC" && <p className="text-zinc-400 font-mono">{formatCurrency(f.misc_rents + f.misc_royalties + f.misc_other_income)}</p>}
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={() => edit1099(f)} size="sm" variant="ghost" className="text-zinc-400 hover:text-white"><Edit className="w-4 h-4" /></Button>
                          <Button onClick={() => delete1099(f.id)} size="sm" variant="ghost" className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-zinc-500 text-sm text-center py-8">No 1099 forms added yet. Click "Add 1099" to enter your investment income.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Form Assistant Tab */}
          <TabsContent value="forms" className="mt-4 space-y-6">
            {/* Progress Steps */}
            <div className="flex items-center justify-center gap-4 mb-6">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    formAssistantStep >= step 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-zinc-800 text-zinc-500'
                  }`}>
                    {formAssistantStep > step ? <CheckCircle className="w-5 h-5" /> : step}
                  </div>
                  {step < 3 && <div className={`w-16 h-1 mx-2 ${formAssistantStep > step ? 'bg-blue-500' : 'bg-zinc-800'}`} />}
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-8 text-xs text-zinc-500 mb-6">
              <span className={formAssistantStep >= 1 ? 'text-blue-400' : ''}>Your Situation</span>
              <span className={formAssistantStep >= 2 ? 'text-blue-400' : ''}>Required Forms</span>
              <span className={formAssistantStep >= 3 ? 'text-blue-400' : ''}>Fill Forms</span>
            </div>

            {/* Step 1: User Profile Questionnaire */}
            {formAssistantStep === 1 && (
              <Card className="bg-[#0A0A0A] border-blue-500/30 rounded-none">
                <CardHeader>
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Bot className="w-5 h-5 text-blue-400" /> Tell Us About Your Tax Situation
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-sm text-zinc-400">Answer these questions to determine which IRS forms you need for 2026.</p>
                  
                  {/* Filing Status */}
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Filing Status</Label>
                    <Select value={userProfile.filing_status} onValueChange={(v) => setUserProfile({...userProfile, filing_status: v})}>
                      <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="form-filing-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                        <SelectItem value="single">Single</SelectItem>
                        <SelectItem value="married_jointly">Married Filing Jointly</SelectItem>
                        <SelectItem value="married_separately">Married Filing Separately</SelectItem>
                        <SelectItem value="head_of_household">Head of Household</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Income Sources */}
                  <div className="space-y-3">
                    <Label className="text-zinc-300 text-sm font-medium">Income Sources</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <label className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-sm cursor-pointer hover:bg-white/10 transition-colors">
                        <input 
                          type="checkbox" 
                          checked={userProfile.has_w2_income}
                          onChange={(e) => setUserProfile({...userProfile, has_w2_income: e.target.checked})}
                          className="w-4 h-4 accent-blue-500"
                        />
                        <span className="text-sm text-white">W-2 Employment Income</span>
                      </label>
                      <label className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-sm cursor-pointer hover:bg-white/10 transition-colors">
                        <input 
                          type="checkbox" 
                          checked={userProfile.has_self_employment}
                          onChange={(e) => setUserProfile({...userProfile, has_self_employment: e.target.checked})}
                          className="w-4 h-4 accent-blue-500"
                        />
                        <span className="text-sm text-white">Self-Employment / 1099 Income</span>
                      </label>
                      <label className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-sm cursor-pointer hover:bg-white/10 transition-colors">
                        <input 
                          type="checkbox" 
                          checked={userProfile.has_rental_property}
                          onChange={(e) => setUserProfile({...userProfile, has_rental_property: e.target.checked})}
                          className="w-4 h-4 accent-blue-500"
                        />
                        <span className="text-sm text-white">Rental Property Income</span>
                      </label>
                      <label className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-sm cursor-pointer hover:bg-white/10 transition-colors">
                        <input 
                          type="checkbox" 
                          checked={userProfile.has_investments}
                          onChange={(e) => setUserProfile({...userProfile, has_investments: e.target.checked})}
                          className="w-4 h-4 accent-blue-500"
                        />
                        <span className="text-sm text-white">Investment Income (Stocks, Crypto, Dividends)</span>
                      </label>
                    </div>
                  </div>

                  {/* Deductions */}
                  <div className="space-y-3">
                    <Label className="text-zinc-300 text-sm font-medium">Deductions & Credits</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <label className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-sm cursor-pointer hover:bg-white/10 transition-colors">
                        <input 
                          type="checkbox" 
                          checked={userProfile.has_itemized_deductions}
                          onChange={(e) => setUserProfile({...userProfile, has_itemized_deductions: e.target.checked})}
                          className="w-4 h-4 accent-blue-500"
                        />
                        <span className="text-sm text-white">Want to Itemize Deductions</span>
                      </label>
                      <label className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-sm cursor-pointer hover:bg-white/10 transition-colors">
                        <input 
                          type="checkbox" 
                          checked={userProfile.has_home_mortgage}
                          onChange={(e) => setUserProfile({...userProfile, has_home_mortgage: e.target.checked})}
                          className="w-4 h-4 accent-blue-500"
                        />
                        <span className="text-sm text-white">Home Mortgage Interest</span>
                      </label>
                      <label className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-sm cursor-pointer hover:bg-white/10 transition-colors">
                        <input 
                          type="checkbox" 
                          checked={userProfile.has_charitable_donations}
                          onChange={(e) => setUserProfile({...userProfile, has_charitable_donations: e.target.checked})}
                          className="w-4 h-4 accent-blue-500"
                        />
                        <span className="text-sm text-white">Charitable Donations</span>
                      </label>
                      <label className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-sm cursor-pointer hover:bg-white/10 transition-colors">
                        <input 
                          type="checkbox" 
                          checked={userProfile.has_retirement_contributions}
                          onChange={(e) => setUserProfile({...userProfile, has_retirement_contributions: e.target.checked})}
                          className="w-4 h-4 accent-blue-500"
                        />
                        <span className="text-sm text-white">Retirement Contributions (401k, IRA)</span>
                      </label>
                      <label className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-sm cursor-pointer hover:bg-white/10 transition-colors">
                        <input 
                          type="checkbox" 
                          checked={userProfile.has_health_savings}
                          onChange={(e) => setUserProfile({...userProfile, has_health_savings: e.target.checked})}
                          className="w-4 h-4 accent-blue-500"
                        />
                        <span className="text-sm text-white">Health Savings Account (HSA)</span>
                      </label>
                      <label className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-sm cursor-pointer hover:bg-white/10 transition-colors">
                        <input 
                          type="checkbox" 
                          checked={userProfile.has_education_expenses}
                          onChange={(e) => setUserProfile({...userProfile, has_education_expenses: e.target.checked})}
                          className="w-4 h-4 accent-blue-500"
                        />
                        <span className="text-sm text-white">Education Expenses</span>
                      </label>
                    </div>
                  </div>

                  {/* Dependents */}
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-sm cursor-pointer hover:bg-white/10 transition-colors">
                      <input 
                        type="checkbox" 
                        checked={userProfile.has_dependents}
                        onChange={(e) => setUserProfile({...userProfile, has_dependents: e.target.checked})}
                        className="w-4 h-4 accent-blue-500"
                      />
                      <span className="text-sm text-white">I have dependents (children under 17)</span>
                    </label>
                    {userProfile.has_dependents && (
                      <div className="ml-7 space-y-2">
                        <Label className="text-zinc-400 text-sm">Number of Dependents</Label>
                        <Input 
                          type="number" 
                          min="1" 
                          max="10"
                          value={userProfile.num_dependents}
                          onChange={(e) => setUserProfile({...userProfile, num_dependents: parseInt(e.target.value) || 0})}
                          className="w-24 bg-black/50 border-white/10 rounded-sm font-mono"
                        />
                      </div>
                    )}
                  </div>

                  <Button 
                    onClick={determineRequiredForms}
                    className="w-full bg-blue-500 text-white hover:bg-blue-600 rounded-sm mt-4"
                    data-testid="determine-forms-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Determine Required Forms
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Step 2: Show Required Forms */}
            {formAssistantStep === 2 && requiredForms && (
              <Card className="bg-[#0A0A0A] border-blue-500/30 rounded-none">
                <CardHeader>
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-400" /> Your Required IRS Forms for 2026
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-zinc-400">Based on your situation, you'll need to file these forms:</p>
                  
                  <div className="space-y-3">
                    {requiredForms.map((form, idx) => (
                      <div key={idx} className="p-4 bg-white/5 border border-white/10 rounded-sm">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-emerald-400" />
                              <span className="text-white font-medium">{form.name}</span>
                              <span className="text-zinc-500">—</span>
                              <span className="text-zinc-400 text-sm">{form.title}</span>
                            </div>
                            <p className="text-xs text-zinc-500 mt-1 ml-6">{form.description}</p>
                          </div>
                          <a 
                            href={form.pdfUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1"
                          >
                            <Download className="w-3 h-3" /> PDF
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-3 mt-6">
                    <Button 
                      onClick={() => setFormAssistantStep(1)}
                      variant="outline"
                      className="flex-1 border-white/20 text-zinc-300 hover:bg-white/10 rounded-sm"
                    >
                      Back
                    </Button>
                    <Button 
                      onClick={fetchAndGenerateFormData}
                      disabled={formLoading}
                      className="flex-1 bg-blue-500 text-white hover:bg-blue-600 rounded-sm"
                      data-testid="fill-forms-btn"
                    >
                      {formLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Analyzing Your Data...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Fill Forms with My WealthHub Data
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Filled Forms Preview */}
            {formAssistantStep === 3 && filledForms && (
              <div className="space-y-6">
                <Card className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border-emerald-500/30 rounded-none">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-8 h-8 text-emerald-400" />
                      <div>
                        <h3 className="text-lg font-bold text-white">Forms Generated Successfully!</h3>
                        <p className="text-sm text-zinc-400">Review the AI-filled forms below. You can copy the data to your tax software.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {Object.entries(filledForms).map(([formKey, formContent]) => (
                  <Card key={formKey} className="bg-[#0A0A0A] border-white/10 rounded-none">
                    <CardHeader>
                      <CardTitle className="text-lg text-white flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-blue-400" />
                          {irsFormsDatabase[formKey]?.name || formKey} - {irsFormsDatabase[formKey]?.title}
                        </span>
                        <div className="flex items-center gap-3">
                          {(formKey === "1040" || formKey === "ScheduleC") && (
                            <Button
                              onClick={() => downloadFilledPDF(formKey)}
                              disabled={formLoading}
                              size="sm"
                              className="bg-emerald-500 text-black hover:bg-emerald-600 rounded-sm text-xs"
                              data-testid={`download-pdf-${formKey}`}
                            >
                              {formLoading ? (
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              ) : (
                                <Download className="w-3 h-3 mr-1" />
                              )}
                              Download Filled PDF
                            </Button>
                          )}
                          <a 
                            href={irsFormsDatabase[formKey]?.pdfUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                          >
                            <ExternalLink className="w-4 h-4" /> Blank Form
                          </a>
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-sm max-h-[500px] overflow-y-auto">
                        <pre className="text-sm text-zinc-300 whitespace-pre-wrap font-mono">{formContent}</pre>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <Card className="bg-amber-500/10 border-amber-500/30 rounded-none">
                  <CardContent className="p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-amber-200 font-medium">Important Disclaimer</p>
                      <p className="text-xs text-amber-300/70 mt-1">
                        This AI-generated form data is for guidance only. Always verify all information before filing. 
                        Tax laws are complex—consult a qualified tax professional for official advice. 
                        WealthHub is not responsible for any errors or omissions.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Button 
                  onClick={resetFormAssistant}
                  variant="outline"
                  className="w-full border-white/20 text-zinc-300 hover:bg-white/10 rounded-sm"
                >
                  Start Over
                </Button>
              </div>
            )}
          </TabsContent>

          {/* 2026 Tax Education Tab */}
          <TabsContent value="education" className="mt-4 space-y-6">
            {/* AI Tax Topic Search */}
            <Card className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/30 rounded-none">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Bot className="w-5 h-5 text-emerald-400" /> Ask AI About 2026 Tax Code
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <Input 
                    value={educationTopic}
                    onChange={(e) => setEducationTopic(e.target.value)}
                    placeholder="e.g., What's new in 2026 tax code? How do I maximize deductions?"
                    className="bg-black/50 border-white/10 rounded-sm flex-1"
                    data-testid="tax-education-input"
                    onKeyPress={(e) => e.key === 'Enter' && handleEducationSearch()}
                  />
                  <Button 
                    onClick={handleEducationSearch}
                    disabled={loading}
                    className="bg-emerald-500 text-black hover:bg-emerald-600 rounded-sm px-6"
                    data-testid="tax-education-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {loading ? "Learning..." : "Educate Me"}
                  </Button>
                </div>
                {educationResult && (
                  <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-sm mt-4 max-h-[400px] overflow-y-auto">
                    <div className="text-sm text-zinc-300 whitespace-pre-wrap">{educationResult}</div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 2026 Tax Brackets */}
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardHeader 
                className="cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => setExpandedSection(expandedSection === 'brackets' ? null : 'brackets')}
              >
                <CardTitle className="text-lg text-white flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-emerald-400" /> 2026 Federal Tax Brackets
                  </span>
                  {expandedSection === 'brackets' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </CardTitle>
              </CardHeader>
              {expandedSection === 'brackets' && (
                <CardContent className="space-y-6">
                  <div>
                    <h4 className="text-sm font-medium text-emerald-400 mb-3">Single Filers</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left py-2 text-zinc-400">Rate</th>
                            <th className="text-left py-2 text-zinc-400">Income Range</th>
                            <th className="text-left py-2 text-zinc-400">Tax Calculation</th>
                          </tr>
                        </thead>
                        <tbody>
                          {taxBrackets2026.single.map((bracket, i) => (
                            <tr key={i} className="border-b border-white/5">
                              <td className="py-2 text-emerald-400 font-mono">{bracket.rate}</td>
                              <td className="py-2 text-white">{bracket.range}</td>
                              <td className="py-2 text-zinc-400 text-xs">{bracket.tax}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-emerald-400 mb-3">Married Filing Jointly</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left py-2 text-zinc-400">Rate</th>
                            <th className="text-left py-2 text-zinc-400">Income Range</th>
                            <th className="text-left py-2 text-zinc-400">Tax Calculation</th>
                          </tr>
                        </thead>
                        <tbody>
                          {taxBrackets2026.married_jointly.map((bracket, i) => (
                            <tr key={i} className="border-b border-white/5">
                              <td className="py-2 text-emerald-400 font-mono">{bracket.rate}</td>
                              <td className="py-2 text-white">{bracket.range}</td>
                              <td className="py-2 text-zinc-400 text-xs">{bracket.tax}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Standard Deductions */}
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardHeader 
                className="cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => setExpandedSection(expandedSection === 'deductions' ? null : 'deductions')}
              >
                <CardTitle className="text-lg text-white flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-400" /> 2026 Standard Deductions
                  </span>
                  {expandedSection === 'deductions' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </CardTitle>
              </CardHeader>
              {expandedSection === 'deductions' && (
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {standardDeductions2026.map((item, i) => (
                      <div key={i} className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-sm">
                        <p className="text-sm text-zinc-400">{item.status}</p>
                        <p className="text-2xl font-bold text-white mt-1">{item.amount}</p>
                        <p className="text-xs text-emerald-400 mt-1">{item.increase}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-zinc-500 mt-4">
                    Additional $1,950 for blind/elderly (single) or $1,550 (married). Itemize if deductions exceed standard.
                  </p>
                </CardContent>
              )}
            </Card>

            {/* Retirement Contribution Limits */}
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardHeader 
                className="cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => setExpandedSection(expandedSection === 'retirement' ? null : 'retirement')}
              >
                <CardTitle className="text-lg text-white flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-violet-400" /> 2026 Retirement Contribution Limits
                  </span>
                  {expandedSection === 'retirement' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </CardTitle>
              </CardHeader>
              {expandedSection === 'retirement' && (
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-2 text-zinc-400">Account Type</th>
                          <th className="text-left py-2 text-zinc-400">2026 Limit</th>
                          <th className="text-left py-2 text-zinc-400">Catch-Up</th>
                        </tr>
                      </thead>
                      <tbody>
                        {retirementLimits2026.map((item, i) => (
                          <tr key={i} className="border-b border-white/5">
                            <td className="py-3 text-white">{item.account}</td>
                            <td className="py-3 text-emerald-400 font-mono">{item.limit}</td>
                            <td className="py-3 text-zinc-400">{item.catchUp}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-zinc-500 mt-4">
                    💡 Tip: Max out employer match first, then consider Roth IRA, then additional 401(k) contributions.
                  </p>
                </CardContent>
              )}
            </Card>

            {/* Tax Credits */}
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardHeader 
                className="cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => setExpandedSection(expandedSection === 'credits' ? null : 'credits')}
              >
                <CardTitle className="text-lg text-white flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-400" /> 2026 Tax Credits
                  </span>
                  {expandedSection === 'credits' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </CardTitle>
              </CardHeader>
              {expandedSection === 'credits' && (
                <CardContent>
                  <div className="space-y-3">
                    {taxCredits2026.map((item, i) => (
                      <div key={i} className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-sm">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-white font-medium">{item.credit}</p>
                            <p className="text-xs text-zinc-400 mt-1">{item.requirement}</p>
                          </div>
                          <span className="text-emerald-400 font-mono text-sm whitespace-nowrap">{item.amount}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-zinc-500 mt-4">
                    ⚠️ Credits reduce tax dollar-for-dollar. Some are refundable (you get money back even if no tax owed).
                  </p>
                </CardContent>
              )}
            </Card>

            {/* What's New in 2026 */}
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardHeader 
                className="cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => setExpandedSection(expandedSection === 'new' ? null : 'new')}
              >
                <CardTitle className="text-lg text-white flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-rose-400" /> What's New for 2026
                  </span>
                  {expandedSection === 'new' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </CardTitle>
              </CardHeader>
              {expandedSection === 'new' && (
                <CardContent>
                  <div className="space-y-4">
                    {newFor2026.map((item, i) => (
                      <div key={i} className="p-4 bg-rose-500/5 border border-rose-500/20 rounded-sm">
                        <h4 className="text-white font-medium">{item.title}</h4>
                        <p className="text-sm text-zinc-400 mt-1">{item.description}</p>
                        <p className="text-xs text-rose-400 mt-2">Impact: {item.impact}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Key Deadlines */}
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardHeader 
                className="cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => setExpandedSection(expandedSection === 'deadlines' ? null : 'deadlines')}
              >
                <CardTitle className="text-lg text-white flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-cyan-400" /> 2026 Key Tax Deadlines
                  </span>
                  {expandedSection === 'deadlines' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </CardTitle>
              </CardHeader>
              {expandedSection === 'deadlines' && (
                <CardContent>
                  <div className="space-y-3">
                    {keyDeadlines2026.map((item, i) => (
                      <div key={i} className="flex gap-4 p-3 bg-cyan-500/5 border border-cyan-500/20 rounded-sm">
                        <div className="w-32 flex-shrink-0">
                          <p className="text-cyan-400 font-mono text-sm">{item.date}</p>
                        </div>
                        <div>
                          <p className="text-white font-medium">{item.event}</p>
                          <p className="text-xs text-zinc-400">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Quick Links */}
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <ExternalLink className="w-5 h-5 text-zinc-400" /> Official IRS Resources
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <a href="https://www.irs.gov/filing" target="_blank" rel="noopener noreferrer" 
                     className="p-3 bg-white/5 border border-white/10 rounded-sm hover:bg-white/10 transition-colors flex items-center gap-2">
                    <ExternalLink className="w-4 h-4 text-zinc-400" />
                    <span className="text-sm text-white">IRS Filing Info</span>
                  </a>
                  <a href="https://www.irs.gov/forms-instructions" target="_blank" rel="noopener noreferrer"
                     className="p-3 bg-white/5 border border-white/10 rounded-sm hover:bg-white/10 transition-colors flex items-center gap-2">
                    <ExternalLink className="w-4 h-4 text-zinc-400" />
                    <span className="text-sm text-white">Forms & Instructions</span>
                  </a>
                  <a href="https://www.irs.gov/refunds" target="_blank" rel="noopener noreferrer"
                     className="p-3 bg-white/5 border border-white/10 rounded-sm hover:bg-white/10 transition-colors flex items-center gap-2">
                    <ExternalLink className="w-4 h-4 text-zinc-400" />
                    <span className="text-sm text-white">Check Refund Status</span>
                  </a>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tax Liability Estimator Tab */}
          <TabsContent value="estimate" className="mt-4 space-y-6">
            {/* Auto-Calculate Section */}
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-violet-500" /> Tax Liability Estimator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-violet-500/10 border border-violet-500/20 rounded-sm">
                  <p className="text-sm text-zinc-300">Automatically calculates your estimated federal and state tax using your W-2 and 1099 data. Uses 2025 IRS tax brackets.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Filing Status</Label>
                    <Select value={liabilityForm.filing_status} onValueChange={(v) => setLiabilityForm({...liabilityForm, filing_status: v})}>
                      <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="liability-filing-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                        <SelectItem value="single">Single</SelectItem>
                        <SelectItem value="married_jointly">Married Filing Jointly</SelectItem>
                        <SelectItem value="married_separately">Married Filing Separately</SelectItem>
                        <SelectItem value="head_of_household">Head of Household</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">State</Label>
                    <Select value={liabilityForm.state || "none"} onValueChange={(v) => setLiabilityForm({...liabilityForm, state: v === "none" ? "" : v})}>
                      <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="liability-state-select">
                        <SelectValue placeholder="Federal Only" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                        <SelectItem value="none">Federal Only</SelectItem>
                        <SelectItem value="CT">Connecticut</SelectItem>
                        <SelectItem value="MA">Massachusetts</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Itemized Deductions</Label>
                    <Input
                      type="number"
                      value={liabilityForm.additional_deductions}
                      onChange={(e) => setLiabilityForm({...liabilityForm, additional_deductions: e.target.value})}
                      placeholder="Standard"
                      className="bg-black/50 border-white/10 rounded-sm font-mono"
                      data-testid="liability-deductions-input"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={handleTaxLiabilityEstimate}
                      disabled={liabilityLoading}
                      className="w-full bg-violet-500 text-white hover:bg-violet-600 rounded-sm h-10"
                      data-testid="calculate-liability-btn"
                    >
                      {liabilityLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calculator className="w-4 h-4 mr-2" />}
                      {liabilityLoading ? "Calculating..." : "Calculate My Tax"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Results */}
            {liabilityResult && (
              <>
                {/* Refund / Owed Banner */}
                <Card className={`border rounded-none ${(liabilityResult.state_tax ? liabilityResult.result.combined_is_refund : liabilityResult.result.is_refund) ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div>
                        {liabilityResult.state_tax ? (
                          <>
                            <p className="text-sm text-zinc-400 uppercase tracking-wider">
                              {liabilityResult.result.combined_is_refund ? "Combined Estimated Refund" : "Combined Estimated Owed"}
                            </p>
                            <p className={`text-4xl font-bold font-mono mt-1 ${liabilityResult.result.combined_is_refund ? 'text-emerald-400' : 'text-red-400'}`} data-testid="liability-result-amount">
                              {liabilityResult.result.combined_is_refund ? '+' : '-'}{formatCurrency(Math.abs(liabilityResult.result.combined_balance))}
                            </p>
                            <div className="flex gap-4 mt-2">
                              <span className={`text-sm font-mono ${liabilityResult.result.is_refund ? 'text-emerald-400/70' : 'text-red-400/70'}`}>
                                Federal: {liabilityResult.result.is_refund ? '+' : '-'}{formatCurrency(Math.abs(liabilityResult.result.balance))}
                              </span>
                              <span className={`text-sm font-mono ${liabilityResult.state_tax.state_is_refund ? 'text-emerald-400/70' : 'text-red-400/70'}`}>
                                {liabilityResult.state_tax.state}: {liabilityResult.state_tax.state_is_refund ? '+' : '-'}{formatCurrency(Math.abs(liabilityResult.state_tax.state_balance))}
                              </span>
                            </div>
                          </>
                        ) : (
                          <>
                            <p className="text-sm text-zinc-400 uppercase tracking-wider">
                              {liabilityResult.result.is_refund ? "Estimated Refund" : "Estimated Amount Owed"}
                            </p>
                            <p className={`text-4xl font-bold font-mono mt-1 ${liabilityResult.result.is_refund ? 'text-emerald-400' : 'text-red-400'}`} data-testid="liability-result-amount">
                              {liabilityResult.result.is_refund ? '+' : '-'}{formatCurrency(Math.abs(liabilityResult.result.balance))}
                            </p>
                          </>
                        )}
                      </div>
                      <div className="text-right space-y-1">
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-zinc-500">Effective Rate</span>
                          <span className="text-lg font-mono text-white" data-testid="effective-rate">{liabilityResult.result.effective_rate}%</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-zinc-500">Marginal Rate</span>
                          <span className="text-lg font-mono text-white">{liabilityResult.result.marginal_rate}%</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-zinc-500">Data Sources</span>
                          <span className="text-xs text-zinc-300">{liabilityResult.data_sources.w2_count} W-2, {liabilityResult.data_sources.form_1099_count} 1099</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Income Breakdown */}
                  <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                    <CardHeader>
                      <CardTitle className="text-sm text-white flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-amber-400" /> Income Sources
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {[
                          { label: "W-2 Wages", value: liabilityResult.income_sources.w2_wages, color: "text-emerald-400" },
                          { label: "Ordinary Dividends", value: liabilityResult.income_sources.ordinary_dividends, color: "text-purple-400" },
                          { label: "Interest Income", value: liabilityResult.income_sources.interest_income, color: "text-cyan-400" },
                          { label: "Short-Term Capital Gains", value: liabilityResult.income_sources.short_term_gains, color: "text-amber-400" },
                          { label: "Long-Term Capital Gains", value: liabilityResult.income_sources.long_term_gains, color: "text-amber-400" },
                          { label: "Self-Employment (1099-NEC)", value: liabilityResult.income_sources.nec_income, color: "text-rose-400" },
                          { label: "Miscellaneous Income", value: liabilityResult.income_sources.misc_income, color: "text-zinc-400" },
                          { label: "Retirement Distributions", value: liabilityResult.income_sources.retirement_distributions, color: "text-blue-400" },
                        ].filter(i => i.value !== 0).map((item) => (
                          <div key={item.label} className="flex justify-between items-center py-1.5 border-b border-white/5">
                            <span className="text-sm text-zinc-400">{item.label}</span>
                            <span className={`font-mono text-sm ${item.color}`}>{formatCurrency(item.value)}</span>
                          </div>
                        ))}
                        <div className="flex justify-between items-center pt-2 border-t border-white/20">
                          <span className="text-sm font-medium text-white">Gross Income</span>
                          <span className="font-mono text-sm text-white font-bold">{formatCurrency(liabilityResult.calculations.gross_income)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Deductions & AGI */}
                  <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                    <CardHeader>
                      <CardTitle className="text-sm text-white flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-emerald-400" /> Deductions & Taxable Income
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                          <span className="text-sm text-zinc-400">Gross Income</span>
                          <span className="font-mono text-sm text-white">{formatCurrency(liabilityResult.calculations.gross_income)}</span>
                        </div>
                        {liabilityResult.calculations.retirement_401k > 0 && (
                          <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                            <span className="text-sm text-zinc-400">401(k) Contributions</span>
                            <span className="font-mono text-sm text-red-400">-{formatCurrency(liabilityResult.calculations.retirement_401k)}</span>
                          </div>
                        )}
                        {liabilityResult.calculations.se_tax_deduction > 0 && (
                          <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                            <span className="text-sm text-zinc-400">SE Tax Deduction</span>
                            <span className="font-mono text-sm text-red-400">-{formatCurrency(liabilityResult.calculations.se_tax_deduction)}</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                          <span className="text-sm text-zinc-400">Adjusted Gross Income</span>
                          <span className="font-mono text-sm text-white">{formatCurrency(liabilityResult.calculations.agi)}</span>
                        </div>
                        <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                          <span className="text-sm text-zinc-400">
                            {liabilityResult.calculations.using_standard_deduction ? "Standard Deduction" : "Itemized Deductions"}
                          </span>
                          <span className="font-mono text-sm text-red-400">-{formatCurrency(liabilityResult.calculations.deduction_amount)}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-white/20">
                          <span className="text-sm font-medium text-white">Taxable Income</span>
                          <span className="font-mono text-sm text-white font-bold">{formatCurrency(liabilityResult.calculations.taxable_income)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Tax Bracket Breakdown */}
                  <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                    <CardHeader>
                      <CardTitle className="text-sm text-white flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-violet-400" /> Federal Tax Brackets
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-1.5">
                        {liabilityResult.tax_breakdown.bracket_breakdown.map((b, i) => (
                          <div key={i} className="relative">
                            <div className="flex justify-between items-center py-1.5">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300">{(b.rate * 100).toFixed(0)}%</span>
                                <span className="text-xs text-zinc-500">{formatCurrency(b.range_low)} - {formatCurrency(b.range_high)}</span>
                              </div>
                              <span className="font-mono text-sm text-white">{formatCurrency(b.tax)}</span>
                            </div>
                            <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                              <div className="h-full bg-violet-500/50 rounded-full" style={{ width: `${(b.income_in_bracket / liabilityResult.calculations.ordinary_taxable) * 100}%` }} />
                            </div>
                          </div>
                        ))}
                        {liabilityResult.tax_breakdown.ltcg_breakdown.length > 0 && (
                          <>
                            <p className="text-xs text-zinc-500 pt-2 mt-2 border-t border-white/5">Capital Gains / Qualified Dividends</p>
                            {liabilityResult.tax_breakdown.ltcg_breakdown.map((b, i) => (
                              <div key={`ltcg-${i}`} className="flex justify-between items-center py-1.5">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">{(b.rate * 100).toFixed(0)}%</span>
                                  <span className="text-xs text-zinc-500">{formatCurrency(b.income)} at preferential rate</span>
                                </div>
                                <span className="font-mono text-sm text-white">{formatCurrency(b.tax)}</span>
                              </div>
                            ))}
                          </>
                        )}
                        <div className="flex justify-between items-center pt-2 mt-1 border-t border-white/20">
                          <span className="text-sm font-medium text-white">Ordinary Income Tax</span>
                          <span className="font-mono text-sm text-white font-bold">{formatCurrency(liabilityResult.tax_breakdown.ordinary_income_tax)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Tax Summary & Withholding */}
                  <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                    <CardHeader>
                      <CardTitle className="text-sm text-white flex items-center gap-2">
                        <FileText className="w-4 h-4 text-cyan-400" /> Tax Summary & Withholding
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                          <span className="text-sm text-zinc-400">Ordinary Income Tax</span>
                          <span className="font-mono text-sm text-white">{formatCurrency(liabilityResult.tax_breakdown.ordinary_income_tax)}</span>
                        </div>
                        {liabilityResult.tax_breakdown.preferential_tax > 0 && (
                          <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                            <span className="text-sm text-zinc-400">Capital Gains / Dividend Tax</span>
                            <span className="font-mono text-sm text-white">{formatCurrency(liabilityResult.tax_breakdown.preferential_tax)}</span>
                          </div>
                        )}
                        {liabilityResult.tax_breakdown.self_employment_tax > 0 && (
                          <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                            <span className="text-sm text-zinc-400">Self-Employment Tax</span>
                            <span className="font-mono text-sm text-white">{formatCurrency(liabilityResult.tax_breakdown.self_employment_tax)}</span>
                          </div>
                        )}
                        {liabilityResult.tax_breakdown.niit > 0 && (
                          <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                            <span className="text-sm text-zinc-400">Net Investment Income Tax</span>
                            <span className="font-mono text-sm text-white">{formatCurrency(liabilityResult.tax_breakdown.niit)}</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center py-1.5 border-b border-white/10">
                          <span className="text-sm font-medium text-white">Total Tax</span>
                          <span className="font-mono text-sm text-red-400 font-bold">{formatCurrency(liabilityResult.tax_breakdown.total_tax)}</span>
                        </div>
                        <p className="text-xs text-zinc-500 pt-1">Withholding</p>
                        {liabilityResult.withholding.w2_federal > 0 && (
                          <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                            <span className="text-sm text-zinc-400">W-2 Federal Withheld</span>
                            <span className="font-mono text-sm text-emerald-400">{formatCurrency(liabilityResult.withholding.w2_federal)}</span>
                          </div>
                        )}
                        {liabilityResult.withholding.from_1099 > 0 && (
                          <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                            <span className="text-sm text-zinc-400">1099 Withholding</span>
                            <span className="font-mono text-sm text-emerald-400">{formatCurrency(liabilityResult.withholding.from_1099)}</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center py-1.5 border-b border-white/10">
                          <span className="text-sm font-medium text-white">Total Withheld</span>
                          <span className="font-mono text-sm text-emerald-400 font-bold">{formatCurrency(liabilityResult.withholding.total_federal_withheld)}</span>
                        </div>
                        <div className={`flex justify-between items-center pt-3 px-3 py-2 rounded-sm ${liabilityResult.result.is_refund ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                          <span className={`text-sm font-bold ${liabilityResult.result.is_refund ? 'text-emerald-400' : 'text-red-400'}`}>
                            {liabilityResult.result.is_refund ? "ESTIMATED REFUND" : "ESTIMATED OWED"}
                          </span>
                          <span className={`font-mono text-lg font-bold ${liabilityResult.result.is_refund ? 'text-emerald-400' : 'text-red-400'}`}>
                            {formatCurrency(Math.abs(liabilityResult.result.balance))}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* State Tax Card */}
                {liabilityResult.state_tax && (
                  <Card className="bg-[#0A0A0A] border-orange-500/20 rounded-none">
                    <CardHeader>
                      <CardTitle className="text-sm text-white flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-orange-400" /> {liabilityResult.state_tax.state_name} State Tax
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                          <span className="text-sm text-zinc-400">Federal AGI</span>
                          <span className="font-mono text-sm text-white">{formatCurrency(liabilityResult.calculations.agi)}</span>
                        </div>
                        <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                          <span className="text-sm text-zinc-400">Personal Exemption</span>
                          <span className="font-mono text-sm text-red-400">-{formatCurrency(liabilityResult.state_tax.personal_exemption)}</span>
                        </div>
                        <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                          <span className="text-sm text-zinc-400">State Taxable Income</span>
                          <span className="font-mono text-sm text-white">{formatCurrency(liabilityResult.state_tax.taxable_income)}</span>
                        </div>
                        <p className="text-xs text-zinc-500 pt-1">State Brackets</p>
                        {liabilityResult.state_tax.bracket_breakdown.map((b, i) => (
                          <div key={i} className="relative">
                            <div className="flex justify-between items-center py-1">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-300">{(b.rate * 100).toFixed(2)}%</span>
                                <span className="text-xs text-zinc-500">{formatCurrency(b.range_low)} - {formatCurrency(b.range_high)}</span>
                              </div>
                              <span className="font-mono text-sm text-white">{formatCurrency(b.tax)}</span>
                            </div>
                            <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                              <div className="h-full bg-orange-500/50 rounded-full" style={{ width: `${liabilityResult.state_tax.taxable_income > 0 ? (b.income_in_bracket / liabilityResult.state_tax.taxable_income) * 100 : 0}%` }} />
                            </div>
                          </div>
                        ))}
                        {liabilityResult.state_tax.surcharge > 0 && (
                          <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                            <span className="text-sm text-zinc-400">High-Income Surcharge</span>
                            <span className="font-mono text-sm text-orange-400">{formatCurrency(liabilityResult.state_tax.surcharge)}</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center pt-2 border-t border-white/20">
                          <span className="text-sm font-medium text-white">Total State Tax</span>
                          <span className="font-mono text-sm text-red-400 font-bold">{formatCurrency(liabilityResult.state_tax.state_tax)}</span>
                        </div>
                        <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                          <span className="text-sm text-zinc-400">State Withheld (W-2)</span>
                          <span className="font-mono text-sm text-emerald-400">{formatCurrency(liabilityResult.state_tax.state_withheld)}</span>
                        </div>
                        <div className={`flex justify-between items-center pt-2 px-3 py-2 rounded-sm ${liabilityResult.state_tax.state_is_refund ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                          <span className={`text-sm font-bold ${liabilityResult.state_tax.state_is_refund ? 'text-emerald-400' : 'text-red-400'}`}>
                            STATE {liabilityResult.state_tax.state_is_refund ? "REFUND" : "OWED"}
                          </span>
                          <span className={`font-mono text-lg font-bold ${liabilityResult.state_tax.state_is_refund ? 'text-emerald-400' : 'text-red-400'}`}>
                            {formatCurrency(Math.abs(liabilityResult.state_tax.state_balance))}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Download + Disclaimer */}
                <div className="flex items-start gap-4">
                  <div className="flex-1 p-3 bg-amber-500/5 border border-amber-500/20 rounded-sm">
                    <p className="text-xs text-amber-400/80">
                      <AlertCircle className="w-3 h-3 inline mr-1" />
                      This is an estimate based on your entered W-2 and 1099 data using 2025 federal{liabilityResult.state_tax ? ` and ${liabilityResult.state_tax.state_name}` : ''} tax brackets. It does not include credits (Child Tax Credit, Earned Income Credit, etc.), AMT, or other adjustments. Consult a tax professional for accurate filing.
                    </p>
                  </div>
                  <Button onClick={downloadTaxReport} variant="outline" className="border-white/10 text-zinc-300 hover:text-white rounded-sm shrink-0" data-testid="download-tax-report-btn">
                    <Download className="w-4 h-4 mr-2" /> Download Report
                  </Button>
                </div>
              </>
            )}

            {/* AI Tax Estimate (below) */}
            {!liabilityResult && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                  <CardHeader>
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                      <Bot className="w-5 h-5 text-violet-500" /> AI Tax Estimate (Manual)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-xs text-zinc-500">Or manually enter income for an AI-powered estimate</p>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Filing Status</Label>
                      <Select value={taxForm.filing_status} onValueChange={(v) => setTaxForm({...taxForm, filing_status: v})}>
                        <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="filing-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-white/10">
                          <SelectItem value="single">Single</SelectItem>
                          <SelectItem value="married_jointly">Married Filing Jointly</SelectItem>
                          <SelectItem value="married_separately">Married Filing Separately</SelectItem>
                          <SelectItem value="head_of_household">Head of Household</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Total Annual Income</Label>
                      <Input type="number" value={taxForm.total_income} onChange={(e) => setTaxForm({...taxForm, total_income: e.target.value})} placeholder="75000" className="bg-black/50 border-white/10 rounded-sm font-mono" data-testid="total-income-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Business/Self-Employment Income</Label>
                      <Input type="number" value={taxForm.business_income} onChange={(e) => setTaxForm({...taxForm, business_income: e.target.value})} placeholder="0" className="bg-black/50 border-white/10 rounded-sm font-mono" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Estimated Deductions</Label>
                      <Input type="number" value={taxForm.deductions} onChange={(e) => setTaxForm({...taxForm, deductions: e.target.value})} placeholder="14600" className="bg-black/50 border-white/10 rounded-sm font-mono" />
                    </div>
                    <Button onClick={handleTaxEstimate} disabled={loading} className="w-full bg-violet-500 text-white hover:bg-violet-600 rounded-sm" data-testid="calculate-tax-btn">
                      <Sparkles className="w-4 h-4 mr-2" />
                      {loading ? "Calculating..." : "AI Tax Estimate"}
                    </Button>
                  </CardContent>
                </Card>
                {result && (
                  <Card className="bg-[#0A0A0A] border-violet-500/30 rounded-none">
                    <CardHeader>
                      <CardTitle className="text-lg text-white flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-violet-500" /> AI Tax Analysis
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="p-4 bg-violet-500/10 border border-violet-500/30 rounded-sm max-h-[500px] overflow-y-auto">
                        <div className="text-sm text-zinc-300 whitespace-pre-wrap">{result}</div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          {/* IRS Search Tab */}
          <TabsContent value="irs" className="mt-4">
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Search className="w-5 h-5 text-amber-500" /> IRS Rules & Regulations Search
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <Input 
                    value={irsQuery}
                    onChange={(e) => setIrsQuery(e.target.value)}
                    placeholder="e.g., What are the home office deduction rules?"
                    className="bg-black/50 border-white/10 rounded-sm flex-1"
                    data-testid="irs-search-input"
                    onKeyPress={(e) => e.key === 'Enter' && handleIrsSearch()}
                  />
                  <Button 
                    onClick={handleIrsSearch}
                    disabled={loading}
                    className="bg-amber-500 text-black hover:bg-amber-600 rounded-sm px-6"
                    data-testid="irs-search-btn"
                  >
                    {loading ? "Searching..." : "Search"}
                  </Button>
                </div>
                <div className="text-xs text-zinc-500">
                  Ask about tax rules, deductions, IRS forms, filing requirements, deadlines, and more.
                </div>
                {irsResult && (
                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm mt-4 max-h-[500px] overflow-y-auto">
                    <div className="text-sm text-zinc-300 whitespace-pre-wrap">{irsResult}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pricing Analysis Tab */}
          <TabsContent value="pricing" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                <CardHeader>
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" /> AI Pricing Strategy
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Business Type</Label>
                    <Input 
                      value={pricingForm.business_type}
                      onChange={(e) => setPricingForm({...pricingForm, business_type: e.target.value})}
                      placeholder="e.g., Restaurant, Consulting, E-commerce"
                      className="bg-black/50 border-white/10 rounded-sm"
                      data-testid="pricing-business-type"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Product/Service</Label>
                    <Input 
                      value={pricingForm.product_service}
                      onChange={(e) => setPricingForm({...pricingForm, product_service: e.target.value})}
                      placeholder="e.g., Web development services"
                      className="bg-black/50 border-white/10 rounded-sm"
                      data-testid="pricing-product"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Cost Basis ($)</Label>
                    <Input 
                      type="number"
                      value={pricingForm.costs}
                      onChange={(e) => setPricingForm({...pricingForm, costs: e.target.value})}
                      placeholder="500"
                      className="bg-black/50 border-white/10 rounded-sm font-mono"
                      data-testid="pricing-costs"
                    />
                  </div>
                  <Button 
                    onClick={handlePricingAnalysis}
                    disabled={loading}
                    className="w-full bg-primary text-black hover:bg-primary/90 rounded-sm"
                    data-testid="analyze-pricing-btn"
                  >
                    {loading ? "Analyzing..." : "Get Pricing Recommendations"}
                  </Button>
                </CardContent>
              </Card>

              {pricingResult && (
                <Card className="bg-[#0A0A0A] border-primary/30 rounded-none">
                  <CardHeader>
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-primary" /> Pricing Strategy
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="p-4 bg-primary/10 border border-primary/30 rounded-sm max-h-[500px] overflow-y-auto">
                      <div className="text-sm text-zinc-300 whitespace-pre-wrap">{pricingResult}</div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Disclaimer */}
        <Card className="bg-amber-500/10 border-amber-500/30 rounded-none">
          <CardContent className="p-4 flex items-start gap-3">
            <Calculator className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-amber-200 font-medium">Disclaimer</p>
              <p className="text-xs text-amber-300/70 mt-1">
                This AI-powered tax center provides estimates and general guidance only. Tax calculations are based on current IRS guidelines 
                but may not account for all individual circumstances. Always consult a qualified tax professional for official advice.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
